/**
 * React Context for a "view correlation id" that ties together:
 * "element loaded" → "element viewed" → "element clicked".
 */

import React, { createContext, forwardRef, useContext } from 'react';

import { generateUUID } from '../../utils/uuid';

/** @typedef {string} ViewCorrelationId */

/**
 * Context for sharing an ID that ties together element interaction events.
 *
 * Links "element loaded" → "element viewed" → "element clicked" events
 * so you can trace the complete user journey for each tracked element.
 */
const ViewCorrelationIdContext = createContext(
  /** @type {ViewCorrelationId | undefined} */ (undefined),
);

/**
 * Provider that creates/shares a stable correlation id for its subtree.
 * The id remains stable for the lifetime of the provider instance.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components that will share the same correlation ID for their events
 */
export function ViewCorrelationIdProvider({ children }) {
  // Maintains a stable correlation id for the lifetime of the provider instance
  const [viewCorrelationId] = React.useState(() => generateUUID());

  return (
    <ViewCorrelationIdContext.Provider value={viewCorrelationId}>
      {children}
    </ViewCorrelationIdContext.Provider>
  );
}

/**
 * Returns the correlation id, or undefined if no provider above.
 * @returns {ViewCorrelationId | undefined}
 */
export function useViewCorrelationId() {
  return useContext(ViewCorrelationIdContext);
}

/**
 * HOC that wraps a component with ViewCorrelationIdProvider.
 * Solves the React context timing issue where hooks consume context before the provider
 * exists, returning null instead of the correlation ID. Without this HOC, you'd need to
 * manually split components into inner/outer patterns (like ElementViewTracker does).
 *
 * @param {React.ComponentType} WrappedComponent - The component to wrap
 */
export function withViewCorrelationIdProvider(WrappedComponent) {
  const WithProvider = forwardRef(function WithProvider(props, ref) {
    return (
      <ViewCorrelationIdProvider>
        {/* @ts-ignore - ref is fine for function/class components that accept it */}
        <WrappedComponent ref={ref} {...props} />
      </ViewCorrelationIdProvider>
    );
  });

  WithProvider.displayName = `withViewCorrelationIdProvider(${
    WrappedComponent.displayName || WrappedComponent.name || 'Component'
  })`;

  return WithProvider;
}
